// apiClient.js — Centralized API client with global session-expired handling
// Handles both axios and fetch calls. On 401/403, clears auth and redirects to login.

import axios from 'axios';
import { API_BASE_URL } from '../config';

// ─── Session Expired Event ──────────────────────────────────────────────────
// Components can listen for this to show a snackbar before the redirect happens.
export const SESSION_EXPIRED_EVENT = 'session-expired';

let isRedirecting = false; // Prevent multiple redirects firing at once

export const handleSessionExpired = () => {
  if (isRedirecting) return; // Already handling — skip duplicate calls
  isRedirecting = true;

  // Clear all auth data
  localStorage.removeItem('authToken');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('loginType');

  // Dispatch a custom event so any mounted React component can show a snackbar
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));

  // Redirect after a short delay so the snackbar has time to appear
  setTimeout(() => {
    window.location.href = '/login';
  }, 2000);
};

// ─── Axios Instance with Interceptor ────────────────────────────────────────
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Attach auth token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercept 401/403 responses globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      handleSessionExpired();
    }
    return Promise.reject(error);
  }
);

// ─── Fetch Wrapper with Session-Expired Handling ────────────────────────────
// Drop-in replacement for fetch() on authenticated endpoints.
// Automatically adds Authorization header and checks for 401/403.
export const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('authToken');

  const headers = {
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401 || response.status === 403) {
    handleSessionExpired();
    // Throw so callers' catch blocks fire (they won't reach the redirect anyway)
    throw new Error('Session expired');
  }

  return response;
};

export default apiClient;
